import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, X, MapPin, Tag, Loader, AlertCircle, Wallet,
  TrendingUp, TrendingDown, BarChart3, Calendar,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const formatBRL = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const EMPTY_FORM = {
  nome: '',
  descricao: '',
  location_id: '',
  status: 'ativo',
};

const STATUS_COLOR = {
  ativo: 'bg-green-500/20 text-green-300 border-green-500/40',
  inativo: 'bg-gray-500/20 text-gray-300 border-gray-500/40',
};

export default function CentrosCusto() {
  const { user } = useAuth();
  const podeVerDRE = !!(
    user?.is_master || user?.is_master_auxiliar ||
    user?.role === 'admin' ||
    user?.permissions?.acesso_centros_custo
  );

  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // DRE state
  const [dre, setDre] = useState(null);
  const [dreLoading, setDreLoading] = useState(false);
  const [dreInicio, setDreInicio] = useState(firstDayOfMonth());
  const [dreFim, setDreFim] = useState(todayISO());
  const [showCategorias, setShowCategorias] = useState(false);

  const fetchCatalogs = async () => {
    try {
      const [s, l] = await Promise.all([
        fetch(`${API_URL}/api/centros-custo/status`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API_URL}/api/inventario/locations-options`, { headers: authHeaders(), cache: 'no-store' }),
      ]);
      if (s.ok) setStatusOptions(await s.json());
      if (l.ok) setLocations(await l.json());
    } catch (e) { console.error(e); }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterLocation !== 'all') params.append('location_id', filterLocation);
      if (search) params.append('search', search);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await fetch(`${API_URL}/api/centros-custo${qs}`, { headers: authHeaders(), cache: 'no-store' });
      if (r.ok) setItems(await r.json());
    } catch (e) {
      toast.error('Falha ao carregar Centros de Custo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCatalogs(); }, []);
  useEffect(() => { fetchItems(); }, [filterStatus, filterLocation]);

  const fetchDre = useCallback(async () => {
    if (!podeVerDRE) return;
    setDreLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('data_inicio', dreInicio);
      params.append('data_fim', dreFim);
      if (filterLocation !== 'all') params.append('location_id', filterLocation);
      const r = await fetch(`${API_URL}/api/centros-custo/dre?${params.toString()}`, {
        headers: authHeaders(), cache: 'no-store',
      });
      if (r.ok) setDre(await r.json());
    } catch (e) {
      console.error('Erro DRE:', e);
    } finally {
      setDreLoading(false);
    }
  }, [dreInicio, dreFim, filterLocation, podeVerDRE]);

  useEffect(() => { fetchDre(); }, [fetchDre]);

  const onSearchKey = (e) => { if (e.key === 'Enter') fetchItems(); };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nome: it.nome || '',
      descricao: it.descricao || '',
      location_id: it.location_id || '',
      status: it.status || 'ativo',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.nome.trim() || form.nome.trim().length < 2) {
      toast.error('Nome obrigatório (mín. 2 caracteres)');
      return;
    }
    if (!form.location_id) {
      toast.error('Selecione um Local de Operação');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao || null,
        location_id: form.location_id,
        status: form.status,
      };
      const url = editing
        ? `${API_URL}/api/centros-custo/${editing.id}`
        : `${API_URL}/api/centros-custo`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Falha ao salvar');
      }
      toast.success(editing ? 'Centro atualizado' : 'Centro cadastrado');
      closeModal();
      fetchItems();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    try {
      const r = await fetch(`${API_URL}/api/centros-custo/${it.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (r.status === 409) {
        const err = await r.json().catch(() => ({}));
        toast.warning(err.detail || 'Centro tem ativos vinculados — foi inativado');
        setConfirmDelete(null);
        fetchItems();
        return;
      }
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Falha ao excluir');
      }
      toast.success('Centro excluído');
      setConfirmDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const ativosOnly = items.filter((i) => i.status === 'ativo').length;
    return { total, ativosOnly };
  }, [items]);

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="centros-custo-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
            <Wallet size={26} className="text-yellow-400" /> Centros de Custo
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Agrupamento de custos por Local de Operação (fazenda, site, base)
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="new-centro-button"
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
        >
          <Plus size={18} /> Novo Centro
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total cadastrados</div>
          <div className="text-2xl font-bold text-white mt-1" data-testid="kpi-total">{stats.total}</div>
        </div>
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Ativos em uso</div>
          <div className="text-2xl font-bold text-green-400 mt-1" data-testid="kpi-ativos">{stats.ativosOnly}</div>
        </div>
      </div>

      {/* DRE Simplificado */}
      {podeVerDRE && (
        <div className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-4 sm:p-5 space-y-4" data-testid="dre-panel">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                <BarChart3 size={18} className="text-yellow-400" /> DRE Simplificado
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Entradas vs Saídas dos lançamentos vinculados aos centros do escopo selecionado.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Calendar size={12} /> Período:
              </div>
              <input
                type="date"
                value={dreInicio}
                onChange={(e) => setDreInicio(e.target.value)}
                data-testid="dre-data-inicio"
                className="px-2 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-xs focus:border-yellow-500 focus:outline-none"
              />
              <span className="text-gray-500 text-xs">até</span>
              <input
                type="date"
                value={dreFim}
                onChange={(e) => setDreFim(e.target.value)}
                data-testid="dre-data-fim"
                className="px-2 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-xs focus:border-yellow-500 focus:outline-none"
              />
            </div>
          </div>

          {dreLoading ? (
            <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
              <Loader size={16} className="animate-spin mr-2" /> Calculando DRE...
            </div>
          ) : !dre ? (
            <div className="text-center py-4 text-gray-500 text-sm">Sem dados</div>
          ) : (
            <>
              {/* Bloco 8 — Cards de Naturezas (Operacional / Não Operacional / Patrimonial / Passivo) */}
              {dre.blocos && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dre-blocos">
                  <div className="bg-emerald-500/5 border border-emerald-500/40 rounded-lg p-3" data-testid="dre-bloco-operacional">
                    <div className="text-[10px] text-emerald-300 uppercase tracking-wide font-semibold">Resultado Operacional</div>
                    <div className={`text-lg sm:text-xl font-bold mt-1 ${dre.blocos.operacional.resultado >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {formatBRL(dre.blocos.operacional.resultado)}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">
                      Receitas {formatBRL(dre.blocos.operacional.receitas)} − Despesas {formatBRL(dre.blocos.operacional.despesas)}
                    </div>
                  </div>
                  <div className="bg-yellow-500/5 border border-yellow-500/40 rounded-lg p-3" data-testid="dre-bloco-nao-operacional">
                    <div className="text-[10px] text-yellow-300 uppercase tracking-wide font-semibold">Receita Não Operacional</div>
                    <div className="text-lg sm:text-xl font-bold text-yellow-400 mt-1">
                      {formatBRL(dre.blocos.nao_operacional.receitas)}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">Venda de ativo, outros (fora do resultado op.)</div>
                  </div>
                  <div className="bg-blue-500/5 border border-blue-500/40 rounded-lg p-3" data-testid="dre-bloco-patrimonial">
                    <div className="text-[10px] text-blue-300 uppercase tracking-wide font-semibold">Aportes de Sócios</div>
                    <div className="text-lg sm:text-xl font-bold text-blue-400 mt-1">
                      {formatBRL(dre.blocos.patrimonial.aportes)}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">Patrimonial — fora do DRE</div>
                  </div>
                  <div className="bg-purple-500/5 border border-purple-500/40 rounded-lg p-3" data-testid="dre-bloco-passivo">
                    <div className="text-[10px] text-purple-300 uppercase tracking-wide font-semibold">Empréstimos Recebidos</div>
                    <div className="text-lg sm:text-xl font-bold text-purple-400 mt-1">
                      {formatBRL(dre.blocos.passivo.emprestimos)}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1">Sócio + bancário — geram contas a pagar</div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3" data-testid="dre-entradas">
                  <div className="text-xs text-green-300 uppercase tracking-wide flex items-center gap-1">
                    <TrendingUp size={12} /> Entradas
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-green-400 mt-1">{formatBRL(dre.entradas)}</div>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="dre-saidas">
                  <div className="text-xs text-red-300 uppercase tracking-wide flex items-center gap-1">
                    <TrendingDown size={12} /> Saídas
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-red-400 mt-1">{formatBRL(dre.saidas)}</div>
                </div>
                <div
                  className={`border rounded-lg p-3 ${dre.resultado >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`}
                  data-testid="dre-resultado"
                >
                  <div className={`text-xs uppercase tracking-wide flex items-center gap-1 ${dre.resultado >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    <Wallet size={12} /> Resultado
                  </div>
                  <div className={`text-xl sm:text-2xl font-bold mt-1 ${dre.resultado >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatBRL(dre.resultado)}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between text-[11px] text-gray-500">
                <span>
                  {dre.total_lancamentos} lançamento{dre.total_lancamentos === 1 ? '' : 's'}
                  {' · '}
                  {dre.centros_no_escopo} centro{dre.centros_no_escopo === 1 ? '' : 's'} no escopo
                </span>
                {dre.por_categoria.length > 0 && (
                  <button
                    onClick={() => setShowCategorias((s) => !s)}
                    data-testid="dre-toggle-categorias"
                    className="text-yellow-400 hover:text-yellow-300 underline"
                  >
                    {showCategorias ? 'Ocultar' : 'Ver'} detalhamento por categoria
                  </button>
                )}
              </div>
              {showCategorias && dre.por_categoria.length > 0 && (
                <div className="overflow-x-auto border border-gray-800 rounded-lg" data-testid="dre-categorias-table">
                  <table className="w-full text-xs">
                    <thead className="bg-[#0a0a0a] border-b border-gray-800">
                      <tr className="text-left text-gray-500 uppercase">
                        <th className="px-3 py-2">Categoria</th>
                        <th className="px-3 py-2">Tipo</th>
                        <th className="px-3 py-2 text-right">Valor</th>
                        <th className="px-3 py-2 text-right">Qtd</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {dre.por_categoria.map((c, i) => (
                        <tr key={`${c.categoria_id}-${c.tipo}-${i}`}>
                          <td className="px-3 py-2 text-gray-200">{c.categoria_nome}</td>
                          <td className="px-3 py-2">
                            <span className={c.tipo === 'entrada' ? 'text-green-400' : 'text-red-400'}>
                              {c.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                            </span>
                          </td>
                          <td className={`px-3 py-2 text-right font-mono ${c.tipo === 'entrada' ? 'text-green-300' : 'text-red-300'}`}>
                            {formatBRL(c.valor)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-400">{c.qtd}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Buscar por nome ou descrição"
            data-testid="search-input"
            className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none text-sm"
          />
        </div>
        <select
          value={filterLocation}
          onChange={(e) => setFilterLocation(e.target.value)}
          data-testid="filter-location"
          className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
        >
          <option value="all">Todos os Locais</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="filter-status"
          className="px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
        >
          <option value="all">Todos status</option>
          {statusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader size={20} className="animate-spin mr-2" /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-[#111] border border-gray-800 rounded-xl">
          <Tag size={36} className="mx-auto mb-2 text-gray-600" />
          <p>Nenhum Centro de Custo encontrado</p>
          <p className="text-xs mt-1">Cadastre o primeiro clicando em "Novo Centro"</p>
        </div>
      ) : (
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0d0d0d] border-b border-gray-800">
                <tr className="text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3 hidden md:table-cell">Local de Operação</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Descrição</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {items.map((it) => (
                  <tr key={it.id} className="hover:bg-[#0d0d0d]" data-testid={`row-centro-${it.id}`}>
                    <td className="px-4 py-3 text-white font-medium">{it.nome}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-gray-300">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} className="text-gray-500" />
                        {it.location_info?.full_name || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-gray-400 text-xs max-w-xs truncate">
                      {it.descricao || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATUS_COLOR[it.status] || 'bg-gray-500/20 text-gray-300 border-gray-500/40'}`}>
                        {it.status_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(it)}
                          data-testid={`edit-${it.id}`}
                          className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-800 rounded"
                          title="Editar"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(it)}
                          data-testid={`delete-${it.id}`}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded"
                          title="Excluir"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de cadastro/edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-lg" data-testid="centro-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">
                {editing ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-white" data-testid="close-modal">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Nome *</label>
                <input
                  required
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  data-testid="form-nome"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                  placeholder="Ex: CC - Fazenda Guadalupe"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Local de Operação *</label>
                <select
                  required
                  value={form.location_id}
                  onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                  data-testid="form-location"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                >
                  <option value="">— Selecione um local —</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  O Local de Operação é a dimensão geográfica de custos. Você pode ter mais de um centro por local.
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  data-testid="form-status"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm"
                >
                  {statusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Descrição</label>
                <textarea
                  rows={3}
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  data-testid="form-descricao"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none text-sm resize-none"
                  placeholder="Detalhes, escopo do centro..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg"
                  data-testid="cancel-button"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-button"
                  className="px-4 py-2 text-sm bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-semibold rounded-lg"
                >
                  {saving ? 'Salvando...' : (editing ? 'Salvar' : 'Cadastrar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-md p-5">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={24} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-white">Excluir Centro de Custo?</h3>
                <p className="text-sm text-gray-400 mt-1">
                  "<span className="text-white">{confirmDelete.nome}</span>" será marcado como excluído.
                  Se possuir ativos vinculados, será apenas <strong>inativado</strong>.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg"
                data-testid="cancel-delete"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-4 py-2 text-sm bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg"
                data-testid="confirm-delete"
              >
                Confirmar exclusão
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
